import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Slider } from '@/components/ui/slider';
import { MF_DISCLAIMER } from '@/components/ComplianceNotice';

export const formatINR = (n) => `₹${Math.round(Math.max(n, 0)).toLocaleString('en-IN')}`;

const shortINR = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

export const sipFutureValue = (monthly, ratePct, months) => {
  const r = ratePct / 12 / 100;
  if (r === 0) return monthly * months;
  return monthly * ((Math.pow(1 + r, months) - 1) / r) * (1 + r);
};

export const CalcField = ({ label, value, onChange, min, max, step = 1, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-3 gap-4">
      <label className="text-sm font-body font-medium text-[#4A4A4A]">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        onBlur={() => onChange(Math.min(Math.max(value, min), max))}
        className="w-36 px-3 py-2 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body text-right text-sm"
        data-testid={`${testId}-input`}
      />
    </div>
    <Slider
      value={[Math.min(Math.max(value, min), max)]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
      data-testid={`${testId}-slider`}
    />
  </div>
);

export const ResultStat = ({ label, value, testId, accent = false }) => (
  <div className="bg-white rounded-xl p-4 border border-[#E5E5E5]">
    <p className="text-xs font-body text-[#737373] mb-1">{label}</p>
    <p className={`text-lg sm:text-xl font-heading font-semibold ${accent ? 'text-[#D97706]' : 'text-[#A0451E]'}`} data-testid={testId}>
      {value}
    </p>
  </div>
);

export const GrowthChart = ({ data, series, testId }) => (
  <div className="bg-white rounded-xl p-4 border border-[#E5E5E5] h-72" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F0E6DF" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#737373' }} />
        <YAxis tickFormatter={shortINR} tick={{ fontSize: 11, fill: '#737373' }} width={60} />
        <Tooltip formatter={(v) => formatINR(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {series.map((s) => (
          <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} fill={s.color} fillOpacity={0.15} strokeWidth={2} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

export const CalcFooter = ({ testId }) => {
  const navigate = useNavigate();
  return (
    <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
      <p className="text-xs font-body text-[#737373] leading-relaxed max-w-lg" data-testid={`${testId}-disclaimer`}>
        Illustrative returns – not guaranteed. The assumed rate of return is for illustration only and does not indicate the
        performance of any scheme. Actual returns may be higher or lower. {MF_DISCLAIMER}
      </p>
      <button
        onClick={() => navigate('/appointment?service=Mutual+Funds')}
        className="btn-primary whitespace-nowrap"
        data-testid={`${testId}-book-btn`}
      >
        Book a Consultation
      </button>
    </div>
  );
};

export const CalcCard = ({ title, subtitle, testId, children }) => (
  <div className="bg-[#FDF8F5] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]" data-testid={testId}>
    <h3 className="text-2xl font-heading font-medium text-[#A0451E] mb-1">{title}</h3>
    <p className="text-sm font-body text-[#737373] mb-6">{subtitle}</p>
    {children}
  </div>
);

export const SERIES_INVESTED = { key: 'invested', name: 'Amount Invested', color: '#A0451E' };
export const SERIES_VALUE = { key: 'value', name: 'Illustrative Value', color: '#D97706' };
